import useI18n from '@/mixins/useI18n';
import { computed } from 'vue';
import { DAY_MSECS, HALFSHORT_WEEKDAYS, WEEK_MSECS } from '@/models/DateTime';

export enum TimeFormatting {
  ChatMessageOverview = 'chatMessageOverview'
}

const useFormattedDateTime = () => {
  const { locale: i18nLocale } = useI18n();

  const locale = computed(() => {
    if (i18nLocale.value === 'en') return 'en-GB'; // GB hat das "vernünftige" dd/mm/yy Format.
    return i18nLocale.value;
  });

  const toDate = (time: Date | string) => (typeof time === 'string' ? new Date(time) : time);

  const formatSimpleTime = (time: Date): string => new Intl.DateTimeFormat(locale.value, { timeStyle: 'short' }).format(time);

  const formatDayTime = (time: Date): string => `${HALFSHORT_WEEKDAYS[i18nLocale.value][time.getDay()]} ${formatSimpleTime(time)}`;

  const formatDate = (time:Date, short = false):string => {
    const dateFormatted = time.toLocaleString(locale.value, {
      year: short ? '2-digit' : 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return dateFormatted.replaceAll(',', '');
  };

  const formatDateTime = (time:Date): string => {
    const dateFormatted = time.toLocaleString(locale.value, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    return dateFormatted.replaceAll(',', '').replaceAll(' ', '\xa0');
  };

  const timeFormatters = {
    [TimeFormatting.ChatMessageOverview]: {
      formatFullTime: (time: Date): string => {
        const monthName = time.toLocaleDateString(locale.value, { month: 'short' });
        const dateStr = `${time.getDate()}`.padStart(2, '0');
        const yearStr = `${time.getFullYear()}`.substring(2);
        const monthNr = `${time.getMonth() + 1}`.padStart(2, '0');
        return i18nLocale.value === 'en'
          ? `${dateStr}/${monthNr}/${yearStr}`
          : `${dateStr}. ${monthName} ${yearStr}`;
      },
      formatDayMonthTime: (time: Date): string => {
        const monthName = time.toLocaleDateString(locale.value, { month: 'long' });
        const dateStr = `${time.getDate()}`.padStart(2, '0');
        return `${dateStr}. ${monthName}`;
      }
    },
    default: {
      formatFullTime: (time: Date): string => {
        const monthName = time.toLocaleDateString(locale.value, { month: 'short' });
        const dateStr = `${time.getDate()}`.padStart(2, '0');
        const timeStr = formatSimpleTime(time);
        const yearStr = `${time.getFullYear()}`.substring(2);
        const monthNr = `${time.getMonth() + 1}`.padStart(2, '0');
        return i18nLocale.value === 'en'
          ? `${dateStr}/${monthNr}/${yearStr}\xa0\xa0${timeStr}`
          : `${dateStr}. ${monthName} ${yearStr}\xa0\xa0${timeStr}`;
      },
      formatDayMonthTime: (time: Date): string => {
        const monthName = time.toLocaleDateString(locale.value, { month: 'short' });
        const dateStr = `${time.getDate()}`.padStart(2, '0');
        const timeStr = formatSimpleTime(time);
        return i18nLocale.value === 'en'
          ? `${dateStr}. ${monthName}\xa0\xa0${timeStr}`
          : `${dateStr}. ${monthName}.\xa0${timeStr}`;
      }
    }
  };

  const getRelativeTime = (time: Date | string, otherTime: Date | string, sonderLocke?: TimeFormatting):string => {
    time = toDate(time);
    otherTime = toDate(otherTime);
    const distance = otherTime.getTime() - time.getTime();

    const { formatDayMonthTime, formatFullTime } = timeFormatters[sonderLocke ?? 'default'];

    // We allow an offset for the distance to account for differences between server time and client system time.
    // This is relevant after uploading contract document files, where sometimes the 'created' timestamp is AFTER client system time.
    // If at some point this is fixed, we can set the offset to 0. (MB)
    const allowedDistanceOffset = -1000;

    if (distance >= allowedDistanceOffset) {
      if (distance < DAY_MSECS && time.getDate() === otherTime.getDate()) return formatSimpleTime(time);
      if (distance < WEEK_MSECS) return formatDayTime(time);
      if (time.getFullYear() === otherTime.getFullYear()) return formatDayMonthTime(time);
    }
    return formatFullTime(time);
  };

  const getFormattedTimeByAge = (time?: Date | string, sonderLocke?: TimeFormatting): string => (time ? getRelativeTime(time, new Date(), sonderLocke) : '');

  const getFormattedDate = (time?: Date | string, short = false): string => {
    if (!time) return '';
    time = toDate(time);
    return formatDate(time, short);
  };

  const getFormattedDateTime = (time?: Date | string): string => {
    if (!time) return '';
    time = toDate(time);
    return formatDateTime(time);
  };

  const getFormattedTimeRange = (begin: string, end: string): string => `${formatSimpleTime(new Date(begin))} - ${formatSimpleTime(new Date(end))}`;

  return {
    getFormattedTimeByAge, // "10:00" | "Mo 10:00" | "01. Jun  10:00" | "01. Jun 26  10:00"
    getFormattedDate, // "01.06.2026" | short: "01.06.26"
    getFormattedDateTime, // "01.06.2026 10:00"
    getFormattedTimeRange // "10:00 - 11:30"
  };
};

export default useFormattedDateTime;
